import { useState } from 'react'
import cx from 'classnames'

import Alert from 'common/components/alert/Alert'
import Button from 'common/components/button'
import Modal from 'common/components/Modal'
import { allowTabCharacter } from 'common/components/textarea/Textarea'

const MAX_HTML_LENGTH = 100000

type Props = {
  isOpen: boolean
  currentDescription?: string
  onClose: () => void
  onImport: (html: string) => void
}

const PasteHtmlModal = ({
  isOpen,
  currentDescription,
  onClose,
  onImport,
}: Props) => {
  const [html, setHtml] = useState('')
  const hasWarning = !!currentDescription?.trim()

  const handleClose = () => {
    setHtml('')
    onClose()
  }

  const handleImport = () => {
    onImport(html)
    setHtml('')
  }

  return (
    <Modal
      visible={isOpen}
      onClose={handleClose}
      title="Paste HTML content"
      size="large"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!html.trim()}
            onClick={handleImport}
            data-testid="import-html-button"
          >
            Import HTML
          </Button>
        </div>
      }
    >
      <p className="text-base text-base-700 pb-4 shrink-0">
        Paste the HTML of an existing campaign to reuse its structure and copy
        as the starting point for your brief.
      </p>
      {hasWarning && (
        <Alert type="warn" showIcon className="mb-4 shrink-0">
          Importing HTML will generate a new brief description from it,
          replacing what&apos;s there now.
        </Alert>
      )}
      <div
        className={cx('flex gap-6', {
          'h-[calc(100vh-16rem)]': !hasWarning,
          'h-[calc(100vh-21rem)]': hasWarning,
        })}
      >
        <div className="flex-1 flex flex-col min-w-0">
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            onKeyDown={allowTabCharacter}
            placeholder="Paste your HTML..."
            maxLength={MAX_HTML_LENGTH}
            data-testid="paste-html-modal-textarea"
            className="flex-1 min-h-0 w-full resize-none font-mono text-sm bg-gold-40 border-1 border-gold-400 hover:border-gold-500 focus:border-base-700 rounded-sm px-4 py-3 outline-none"
          />
          <p className="text-xs text-base-500 pt-2 shrink-0">
            Personalisation tags like {'{{first_name}}'} are filled with sample
            values in the preview.
          </p>
        </div>
        <div className="flex-1 flex flex-col min-w-0 border-l border-gold-400 pl-6">
          {html.trim() ? (
            <iframe
              srcDoc={html}
              className="w-full flex-1 min-h-0 border border-gold-400 rounded-sm"
              sandbox=""
              title="Pasted HTML preview"
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-lg text-base-700 text-center">
              Paste HTML to see a preview.
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PasteHtmlModal
